// GENERATED FILE — DO NOT EDIT.
// Source: hsos-shared/src/analytics/ — edit there, then run:
//   node scripts/sync-shared-modules.mjs
// Target: hsos-web vendored @hsos/shared

/**
 * Canonical event/person/group property names, and the PII rules that govern
 * what may leave the platform.
 *
 * Helm processes customer phone numbers, addresses and message bodies on behalf
 * of tenants. PostHog is a third-party processor, so the rule is narrow and
 * absolute: **identifiers and aggregates go out, content does not.** Anything
 * derived from an end customer's message, name, address, or phone number is
 * either omitted or reduced to a non-reversible token before capture.
 */

/** Properties stamped on every event by the platform capture helpers. */
export const PLATFORM_PROPERTIES = {
  /** Tenant the event belongs to. Present on every tenant-scoped event. */
  ORGANIZATION_ID: "organization_id",
  /** Which Helm surface emitted the event. */
  SURFACE: "surface",
  /** `production` | `preview` | `development` — keeps environments separable. */
  ENVIRONMENT: "environment",
  /** Deployed git SHA, so a metric change can be tied to a release. */
  RELEASE: "release",
  /** Ties an analytics event back to `events`/`ai_runs` rows in Postgres. */
  CORRELATION_ID: "correlation_id",
} as const;

/** Person properties set on identify. Never include tenant customer data. */
export type HelmPersonProperties = {
  email?: string;
  name?: string;
  /** `owner` | `admin` | `dispatcher` | `tech` | `platform_operator` */
  role?: string;
  /** True for Helm staff — used to exclude internal traffic from funnels. */
  is_platform_operator?: boolean;
  /** The org active at identify time; group analytics remains the source of truth. */
  organization_id?: string;
  /** Set once, on first identify — enables cohort/retention analysis. */
  signed_up_at?: string;
};

/* ── PII egress guard ──────────────────────────────────────────────────────── */

/**
 * Property names that must never be sent to PostHog. Matched case-insensitively
 * against the whole key and as a substring, so `customer_phone` and `phone` both
 * fail. Enforced by `sanitizeProperties`, which every capture helper routes
 * through — a blocked key is dropped, not sent redacted, so nothing about the
 * value leaks through its presence pattern.
 */
export const BLOCKED_PROPERTY_PATTERNS = [
  "phone",
  "address",
  "street",
  "postal",
  "zipcode",
  "message_body",
  "body",
  "transcript",
  "content",
  "password",
  "secret",
  "token",
  "api_key",
  "authorization",
  "ssn",
  "card",
  "cvv",
  "iban",
] as const;

/**
 * Keys exempt from the substring rules above because they are known-safe and
 * would otherwise trip a broad pattern (`content_type` on `content`, and the
 * correlation/lead identifiers on `token`).
 */
const ALLOWED_EXACT_KEYS = new Set<string>([
  "content_type",
  "correlation_id",
  "token_count",
  "input_tokens",
  "output_tokens",
  "address_count",
]);

export function isBlockedPropertyKey(key: string): boolean {
  const normalized = key.toLowerCase();
  if (ALLOWED_EXACT_KEYS.has(normalized)) return false;
  return BLOCKED_PROPERTY_PATTERNS.some((pattern) =>
    normalized.includes(pattern),
  );
}

/**
 * The string list exists for genuinely multi-valued dimensions — the trades a
 * shop works in, where a joined string would force `icontains` filters and
 * quietly stop being a dimension you can break down by.
 *
 * Deliberately `string[]` and not `AnalyticsPropertyValue[]`: PostHog will
 * accept a mixed array, but a property whose element type varies per event is
 * not something its UI can group on, so allowing it only buys a shape nobody
 * can query.
 */
export type AnalyticsPropertyValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | readonly string[];

/** Elements kept per list property. */
const MAX_LIST_LENGTH = 20;

/**
 * Drop blocked keys, drop `undefined`, and cap string length.
 *
 * Long strings are truncated rather than dropped: free-text properties like a
 * demo question are useful in aggregate but must not become an unbounded
 * content channel.
 *
 * Lists are capped the same way and for the same reason — on both axes, since
 * an unbounded array of bounded strings is still unbounded. Empty lists are
 * kept rather than dropped: "this shop works in no known trade" is a real
 * answer and one worth being able to filter for.
 */
export function sanitizeProperties(
  properties: Record<string, AnalyticsPropertyValue> | undefined,
  options: { maxStringLength?: number } = {},
): Record<string, Exclude<AnalyticsPropertyValue, undefined>> {
  if (!properties) return {};
  const maxLength = options.maxStringLength ?? 200;
  const out: Record<string, Exclude<AnalyticsPropertyValue, undefined>> = {};

  const capString = (value: string): string =>
    value.length > maxLength ? value.slice(0, maxLength) : value;

  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined) continue;
    if (isBlockedPropertyKey(key)) continue;

    if (Array.isArray(value)) {
      // `String(el)` before capping rather than trusting the declared element
      // type. This function is the content boundary, and some callers build
      // property records out of request bodies — an element that arrives as an
      // object has no `.length`, so a bare cap would compare `undefined` and
      // wave the whole thing through, which is exactly the unbounded channel
      // the cap exists to close.
      out[key] = value
        .slice(0, MAX_LIST_LENGTH)
        .map((element) => capString(String(element)));
      continue;
    }

    out[key] = typeof value === "string" ? capString(value) : value;
  }

  return out;
}

/**
 * Reduce an identifier to a stable, non-reversible bucket so a phone number or
 * email can still be counted per-person without being stored.
 *
 * FNV-1a: no crypto dependency, identical output in the browser, Node and Deno.
 */
export function hashIdentifier(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36).padStart(7, "0");
}
